import { useEffect, useRef, useState } from 'react';
import { Frame, Grid2x2, Image } from 'lucide-react';
import type { AssetReviewSnapshot } from '@volley/crucible-ipc-types';
import type { CrucibleClient } from '@volley/crucible-client';
import { AssetReviewPanel } from './AssetReviewPanel';
import { ArtGuidedFlow } from './ArtGuidedFlow';
import { shouldGuideArtFlow } from './art-style-lock.js';
import { ArtLibrary } from '../pages/art/ArtLibrary';
import { useCrucibleClient } from '../transport/context.js';

/**
 * The Art tab (crucible#2611 Task 4) — a standalone review session for a
 * BUILT game, independent of any create/build task. Thin renderer shell over
 * the `art:*` IPC surface (crucible#2611 Task 3, `ipc-surfaces/art.ts`):
 * opens a session on mount, then picks the surface to show.
 *
 * crucible#2868 (Stream A / AC-1) — the landing surface BRANCHES on whether an
 * unsatisfied MANDATORY style gate is pending (`shouldGuideArtFlow`, derived
 * from the snapshot fetched at mount), NOT on a manual review/library toggle:
 *
 *   - **Gate pending** (a style-anchor stage exists AND is not yet locked) →
 *     `ArtGuidedFlow`, the Design-skinned guided stage-by-stage flow. No sub-nav
 *     — the guided flow owns the whole staged progression until the gate clears.
 *   - **Otherwise** (anchor locked, OR no anchor stage at all — a done/built or
 *     code-first manifest) → the maintenance surfaces, defaulting to `ArtLibrary`
 *     (the category grid + per-asset inspector). `AssetReviewPanel` (the flat
 *     "Review") stays REACHABLE via a small toggle here, but is not the landing.
 *
 * Guiding ONLY on a pending gate (not merely "style not locked") is deliberate:
 * a fully-built game with NO anchor stage has `isStyleLocked === false` forever,
 * so an is-locked landing trapped it in the guided flow with no maintenance
 * surface (crucible#2872 Bugbot round 4). No empty-wall risk: a genuinely fresh
 * Foundry game HAS an anchor stage (07's derive emits the isStyleAnchor need) so
 * it guides; only a no-anchor manifest reaches the Library (a no-anchor + empty
 * one degrades to the Library's own empty-state, which is correct).
 *
 * The branch is a LANDING decision, computed once per gameId at mount — it does
 * not flip mid-review (locking the anchor part-way through the guided flow must
 * not yank the user into the Library). A completed guided flow lands in the
 * Library the next time the tab is opened.
 *
 * Closes only what it opened, regardless of which surface is active — the
 * surface choice never touches `art:open`/`art:close`.
 *
 * THE ATTACH CONTRACT (see `art.ts`'s `artOpen`/`artClose` docblocks): when
 * a review is already active for this gameId — a build-time review still
 * running, or another Art tab mount from earlier in the session — `art:open`
 * reports `{ok:false, reason:'already-open'}` rather than launching a
 * second engine onto the same manifest. This component's job in that case
 * is to ATTACH: render against the existing session exactly as if it had
 * opened it (both `AssetReviewPanel` and `ArtLibrary`'s `asset-review:*` /
 * `art:*` calls are gameId-scoped and work against any live review for that
 * gameId, whoever opened it) — and, critically, NEVER call `art:close` for a
 * session it did not launch. `ownedRequestRef` is the single source of truth
 * for that distinction.
 */

interface ArtOpenResult {
  ok: boolean;
  reason?: 'no-engine' | 'no-manifest' | 'already-open';
  gameId?: string;
  url?: string;
}

interface ArtOpenRequest {
  client: CrucibleClient;
  gameId: string;
  promise: Promise<ArtOpenResult>;
  closeRequested: boolean;
}

interface ActiveArtEffect {
  token: symbol;
  request: ArtOpenRequest;
}

function closeArtRequest(request: ArtOpenRequest, ownership: 'orphaned' | 'owned'): void {
  if (request.closeRequested) return;
  request.closeRequested = true;
  void request.client.invoke('art:close', { gameId: request.gameId }).catch((err) => {
    console.error(
      `[ArtWorkspace] failed to close an ${ownership} session for ${request.gameId}`,
      err,
    );
  });
}

/**
 * crucible#2868 — fetch a snapshot and derive the landing branch (should the
 * tab guide, or show the maintenance Library?). A session opened by this mount
 * keeps the original safe fallback: an unreadable snapshot resolves to GUIDED.
 * An `already-open` attachment can race the concurrent owner's proxy
 * registration, so that path retries first; exhausting the bounded wait still
 * resolves to GUIDED rather than the Library's empty-placeholder wall.
 */
const ATTACHED_REVIEW_SNAPSHOT_RETRY_INTERVAL_MS = 250;
const ATTACHED_REVIEW_SNAPSHOT_RETRY_COUNT = 80;

function waitForAttachedReviewRetry(signal?: AbortSignal): Promise<boolean> {
  if (signal?.aborted) return Promise.resolve(false);
  return new Promise<boolean>((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = (shouldRetry: boolean): void => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      resolve(shouldRetry);
    };
    const onAbort = (): void => finish(false);
    timer = setTimeout(() => finish(true), ATTACHED_REVIEW_SNAPSHOT_RETRY_INTERVAL_MS);
    signal?.addEventListener('abort', onAbort, { once: true });
    if (signal?.aborted) onAbort();
  });
}

async function fetchShouldGuide(
  client: CrucibleClient,
  gameId: string,
  waitForAttachedReview = false,
  signal?: AbortSignal,
): Promise<boolean> {
  const attempts = waitForAttachedReview ? ATTACHED_REVIEW_SNAPSHOT_RETRY_COUNT : 1;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (signal?.aborted) return true;
    try {
      // crucible#3007 — rewired off the cloud-unreachable `asset-review:snapshot`
      // onto `art:snapshot` (its cloud-reachable twin); same engine data, gameId
      // now required rather than falling back to the most-recently-opened review.
      const snap = await client.invoke('art:snapshot', { gameId });
      return shouldGuideArtFlow(snap);
    } catch {
      if (signal?.aborted) return true;
      if (attempt === attempts - 1) return true;
      if (!(await waitForAttachedReviewRetry(signal))) return true;
    }
  }
  return true;
}

type ArtWorkspaceStatus =
  | { kind: 'opening' }
  /** `owned: true` → this mount's own `art:open` launched the session, and
   *  it (and only it) is responsible for `art:close` on done. `owned: false`
   *  → attached to someone else's already-open session; never closes it.
   *  `showGuided` is the crucible#2868 landing branch (guide vs Library),
   *  computed once from the snapshot fetched right after the session opened. */
  | { kind: 'ready'; owned: boolean; showGuided: boolean }
  | { kind: 'unavailable'; reason: 'no-manifest' | 'no-engine' }
  | { kind: 'error'; message: string };

export interface ArtWorkspaceProps {
  /** kebab-case game id whose generated art this tab reviews. */
  gameId: string;
}

/** crucible#2611 Task 5 — the Art tab's two sub-modes over one open session. */
type ArtSubMode = 'review' | 'library';

export function ArtWorkspace({ gameId }: ArtWorkspaceProps): React.JSX.Element {
  const client = useCrucibleClient();
  const [status, setStatus] = useState<ArtWorkspaceStatus>({ kind: 'opening' });
  // Only relevant in the style-locked (maintenance) branch; defaults to the
  // Library — Review is reachable but is no longer the fresh-game landing.
  const [subMode, setSubMode] = useState<ArtSubMode>('library');
  const openRequestRef = useRef<ArtOpenRequest | null>(null);
  const activeEffectRef = useRef<ActiveArtEffect | null>(null);
  const ownedRequestRef = useRef<ArtOpenRequest | null>(null);

  useEffect(() => {
    let cancelled = false;
    const snapshotRetryController = new AbortController();
    const effectToken = Symbol('ArtWorkspace effect');
    const previousRequest = openRequestRef.current;
    const request =
      previousRequest?.client === client && previousRequest.gameId === gameId
        ? previousRequest
        : {
            client,
            gameId,
            promise: client.invoke('art:open', { gameId }) as Promise<ArtOpenResult>,
            closeRequested: false,
          };
    openRequestRef.current = request;
    activeEffectRef.current = { token: effectToken, request };

    setStatus({ kind: 'opening' });
    // Reset the maintenance sub-mode on every game switch (crucible#2872 Bugbot
    // Medium): without this, a user left on Review for one style-locked game
    // would land on Review for the NEXT locked game instead of the Library
    // default. All other game-scoped state resets here too — `status` (above,
    // re-derived per-game incl. the showGuided branch via fetchShouldGuide) and
    // session ownership (tracked by request identity). subMode was the only one
    // that leaked across a switch.
    setSubMode('library');
    void (async () => {
      try {
        const result = await request.promise;
        if (cancelled) {
          // The component unmounted (or gameId changed, tearing down THIS
          // effect run) while art:open was still in flight. If it landed
          // ok:true, close it unless a StrictMode replay has adopted the exact
          // same request. React replays effects in development without
          // remounting the component, so the refs survive and let the second
          // effect share the in-flight open instead of attaching too early and
          // then losing its server when this cancelled effect closes it.
          if (result.ok && activeEffectRef.current?.request !== request) {
            closeArtRequest(request, 'orphaned');
          }
          return;
        }
        if (result.ok || result.reason === 'already-open') {
          const owned = result.ok;
          if (owned) ownedRequestRef.current = request;
          // The session is live either way; read one snapshot to pick the
          // landing surface (fresh → guided flow, existing → Library).
          // `already-open` can mean a concurrent warm-up owns the core review
          // slot but has not registered its proxy URL yet. A one-shot snapshot
          // in that gap permanently misroutes a completed game to Guided mode.
          // Wait only on the attach path; a session we opened ourselves keeps
          // the existing immediate fail-safe fallback.
          const waitForAttachedReview = !result.ok && result.reason === 'already-open';
          const showGuided = await fetchShouldGuide(
            client,
            gameId,
            waitForAttachedReview,
            snapshotRetryController.signal,
          );
          if (!cancelled) setStatus({ kind: 'ready', owned, showGuided });
          return;
        }
        setStatus({ kind: 'unavailable', reason: result.reason ?? 'no-engine' });
      } catch (err) {
        if (!cancelled) {
          setStatus({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
        }
      }
    })();
    // No BLANKET cleanup-time art:close: navigating away from the Art tab
    // (a mode switch, not a "done") for the SAME game must not tear down a
    // review the user may come straight back to — that's still handled only
    // by `handleDone`, on the mount that owns it. But if THIS instance
    // already owns a successfully-opened session for the OUTGOING gameId
    // (art:open resolved ok:true before cleanup ran) and the user switches
    // games, nobody else will ever close it: a later remount for the OLD
    // gameId attaches via the already-open path and correctly refuses to close
    // a session it didn't launch (crucible#2611 Bugbot Medium — "Art session
    // leaks on game switch"). Same owner-closes-on-the-way-out contract as the
    // orphan-open fix above, just triggered by cleanup instead of a
    // late-resolving promise. Cleanup is deferred by one microtask so a
    // StrictMode replay can adopt the same request first.
    return () => {
      cancelled = true;
      snapshotRetryController.abort();
      if (activeEffectRef.current?.token === effectToken) {
        activeEffectRef.current = null;
      }
      if (ownedRequestRef.current === request) {
        queueMicrotask(() => {
          if (activeEffectRef.current?.request === request) return;
          if (ownedRequestRef.current === request) ownedRequestRef.current = null;
          closeArtRequest(request, 'owned');
        });
      }
    };
  }, [client, gameId]);

  const handleDone = (): void => {
    const ownedRequest = ownedRequestRef.current;
    if (ownedRequest?.client === client && ownedRequest.gameId === gameId) {
      closeArtRequest(ownedRequest, 'owned');
    }
  };

  if (status.kind === 'opening') {
    return (
      <div
        className="flex h-full items-center justify-center gap-3 text-sm text-white/60"
        data-testid="art-workspace-opening"
      >
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-crucible-accent" />
        Opening the art review…
      </div>
    );
  }

  if (status.kind === 'unavailable') {
    return (
      <div
        className="flex h-full items-center justify-center bg-crucible-bg px-6"
        data-testid="art-workspace-unavailable"
      >
        <div className="w-full max-w-xl rounded-lg border border-white/10 bg-white/5 p-4 shadow-2xl">
          <div className="flex items-start gap-3">
            <Frame size={18} strokeWidth={1.8} className="mt-0.5 shrink-0 text-white/40" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white/70">
                {status.reason === 'no-manifest'
                  ? 'No generated art to review yet for this game.'
                  : "Couldn't start the review engine for this game."}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status.kind === 'error') {
    return (
      <div
        role="alert"
        className="flex h-full items-center justify-center bg-crucible-bg px-6"
        data-testid="art-workspace-error"
      >
        <div className="w-full max-w-xl rounded-lg border border-red-500/30 bg-red-500/5 p-4 shadow-2xl">
          <p className="text-sm text-red-300">{status.message}</p>
        </div>
      </div>
    );
  }

  // A mandatory style gate is pending (anchor stage present + not yet locked).
  // Land on the guided flow full-bleed (it owns its own layout + stage rail);
  // no maintenance sub-nav until the gate clears.
  if (status.showGuided) {
    return (
      <div className="h-full" data-testid="art-workspace-guided">
        <ArtGuidedFlow gameId={gameId} onDone={handleDone} />
      </div>
    );
  }

  // No pending gate (anchor locked, or no anchor stage at all — a done/built or
  // code-first game). Maintenance surfaces, defaulting to the Library; Review
  // stays reachable via the toggle but is not the landing.
  return (
    <div
      className="flex h-full flex-col gap-3 overflow-hidden p-4"
      data-testid="art-workspace-maintenance"
    >
      <div className="flex shrink-0 gap-1.5" data-testid="art-workspace-sub-nav">
        <button
          type="button"
          data-testid="art-workspace-sub-nav-library"
          data-active={subMode === 'library'}
          onClick={() => setSubMode('library')}
          className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition ${
            subMode === 'library'
              ? 'bg-crucible-accent text-black'
              : 'bg-white/10 text-white/70 hover:bg-white/15'
          }`}
        >
          <Grid2x2 size={13} strokeWidth={2} />
          Library
        </button>
        <button
          type="button"
          data-testid="art-workspace-sub-nav-review"
          data-active={subMode === 'review'}
          onClick={() => setSubMode('review')}
          className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition ${
            subMode === 'review'
              ? 'bg-crucible-accent text-black'
              : 'bg-white/10 text-white/70 hover:bg-white/15'
          }`}
        >
          <Image size={13} strokeWidth={2} />
          Review
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {subMode === 'review' ? (
          <AssetReviewPanel gameId={gameId} onDone={handleDone} />
        ) : (
          <ArtLibrary gameId={gameId} />
        )}
      </div>
    </div>
  );
}
